import { api, healthCheck } from 'boot/axios'
import ApplicationEnvironment from 'src/models/application-environment/ApplicationEnvironment'
import Event from 'src/models/event/Event'
import Location from 'src/models/event/Location'
import EventJson from 'src/models/event/interfaces/EventJson'
import LocationJson from 'src/models/event/interfaces/LocationJson'
import LockerTypeAvailability from '../models/locker/locker-type-availability/LockerTypeAvailability'
import LockerTypeAvailabilityJson from '../models/locker/locker-type-availability/interfaces/LockerTypeAvailabilityJson'
import RentalWorkflowConfiguration from 'src/models/rental/workflow/RentalWorkflowConfiguration'
import WorkflowConfiguration from 'src/models/workflow/WorkflowConfiguration'
import Money from 'src/models/shared/Money'
import { CalculateLockerTypePriceParameters as RentalCalculateLockerTypePriceParameters } from '../models/rental/interfaces/CalculateLockerTypePriceParameters'
import { CalculateLockerTypePriceParameters as ReservationCalculateLockerTypePriceParameters } from '../models/reservation/interfaces/CalculateLockerTypePriceParameters'
import Order from 'src/models/order/Order'
import RequestRentalParameters from '../models/rental/interfaces/RequestRentalParameters'
import Customer from 'src/models/order/Customer'
import { CountryCode } from 'libphonenumber-js'
import Payment from 'src/models/payment/Payment'
import { OrderItem } from 'src/models/order/OrderItem'
import ErrorHandler from './ErrorHandler'
import { CanceledError } from 'axios'
import ProcessIntentParameters from 'src/models/rental/interfaces/ProcessIntentParameters'
import InitiatePaymentParameters from 'src/models/payment/interfaces/InitiatePaymentParameters'
import Authentication from 'src/models/authentication/Authentication'
import AuthenticationJson from 'src/models/authentication/interfaces/AuthenticationJson'
import ReservationInfo from 'src/models/rental/ReservationInfo'
import ReservationWorkflowConfiguration from '../models/reservation/workflow/ReservationWorkflowConfiguration'
import PlanReservationParameters from '../models/reservation/interfaces/PlanReservationParameters'
import OrderJson from 'src/models/order/interfaces/OrderJson'
import ContentBlock from 'src/models/Content/ContentBlock'
import ContentBlockJson from 'src/models/Content/interfaces/ContentBlockJson'
import { QueryParamMetadata } from 'src/services/handle-metadata'

export async function getHealthCheck(): Promise<boolean> {
  return healthCheck
    .get('')
    .then((response) => response.status === 204)
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}
export function loadEnvironment(
  domain: string,
): Promise<ApplicationEnvironment> {
  return api
    .get('application-environment/domain/' + encodeURIComponent(domain))
    .then((response) => ApplicationEnvironment.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getEventsList(): Promise<Array<Event>> {
  return api
    .get('application-environment/:reference/events')
    .then((response) =>
      response.data.map((item: EventJson) => Event.fromJson(item)),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getLocationsListForEvent(
  eventId: number,
): Promise<Array<Location>> {
  return api
    .get(
      'application-environment/:reference/events/' +
        encodeURIComponent(eventId) +
        '/locations',
    )
    .then((response) =>
      response.data.map((item: LocationJson) => Location.fromJson(item)),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getLocation(locationId: number | string): Promise<Location> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId),
    )
    .then((response) => Location.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getWorkflowConfigurationForLocation(
  locationId: number,
  controller?: AbortController,
): Promise<WorkflowConfiguration | null> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/workflows',
      { signal: controller?.signal },
    )
    .then((response) => WorkflowConfiguration.fromJson(response.data))
    .catch((error) => {
      if (error instanceof CanceledError) {
        return null
      }

      throw ErrorHandler.convert(error)
    })
}

export function getRentalWorkflowConfigurationForLocation(
  locationId: string,
): Promise<RentalWorkflowConfiguration> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/workflows/rental/configuration',
    )
    .then((response) => RentalWorkflowConfiguration.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getReservationWorkflowConfigurationForLocation(
  locationId: string,
): Promise<ReservationWorkflowConfiguration> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/workflows/reservation/configuration',
    )
    .then((response) =>
      ReservationWorkflowConfiguration.fromJson(response.data),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getLockerTypeAvailabilityForRentalWorkflow(
  locationId: string,
  duration: string,
): Promise<Array<LockerTypeAvailability>> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/workflows/rental/locker-type-availability/' +
        encodeURIComponent(duration),
    )
    .then((response) =>
      response.data.map((item: LockerTypeAvailabilityJson) =>
        LockerTypeAvailability.fromJson(item),
      ),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getLockerTypeAvailabilityForReservationWorkflow(
  locationId: string,
  durationStartDate: string,
  durationEndDate: string,
): Promise<Array<LockerTypeAvailability>> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/workflows/reservation/locker-type-availability/' +
        encodeURIComponent(durationStartDate) +
        '/' +
        encodeURIComponent(durationEndDate),
    )
    .then((response) =>
      response.data.map((item: LockerTypeAvailabilityJson) =>
        LockerTypeAvailability.fromJson(item),
      ),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function calculateLockerTypePriceForRentalWorkflow(
  locationId: string,
  params: RentalCalculateLockerTypePriceParameters,
): Promise<Money> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/workflows/rental/calculate-price',
      {
        params: params,
      },
    )
    .then((response) => Money.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function calculateLockerTypePriceForReservationWorkflow(
  locationId: string,
  params: ReservationCalculateLockerTypePriceParameters,
): Promise<Money> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/workflows/reservation/calculate-price',
      {
        params: params,
      },
    )
    .then((response) => Money.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function initiateNewCart(locationId: string | number): Promise<Order> {
  return api
    .post(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/cart/initiate',
    )
    .then((response) => Order.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getOrderByToken(token: string): Promise<Order> {
  return api
    .get(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(token),
    )
    .then((response) => Order.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getOrdersByToken(tokens: string[]): Promise<Order[]> {
  return api
    .get('application-environment/:reference/orders/by-token', {
      params: { tokens: tokens },
    })
    .then((response) =>
      response.data.map((orderData: OrderJson) => Order.fromJson(orderData)),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function requestRental(cart: Order, params: RequestRentalParameters) {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(cart.token) +
        '/rental/request',
      params,
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function planReservation(
  cart: Order,
  params: PlanReservationParameters,
) {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(cart.token) +
        '/reservation/plan',
      params,
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function provideCustomerDetails(cart: Order, customer: Customer) {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(cart.token) +
        '/customer-details',
      customer.toJson(),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function subscribeCustomerToMarketing(cart: Order) {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(cart.token) +
        '/customer-marketing-subscription',
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getCountryCodeByIp(): Promise<CountryCode | undefined> {
  return api
    .get('https://ip2c.org/s')
    .then((response) => {
      if (response.data[0] == 1) {
        return response.data.substr(2, 2)
      }
    })
    .catch(() => {
      return
    })
}

export function checkoutOrder(cart: Order, metadata: QueryParamMetadata) {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(cart.token) +
        '/checkout',
      { metadata },
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function cancelOrder(cart: Order) {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(cart.token) +
        '/cancel',
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function initialisePayment(
  order: Order,
  params: InitiatePaymentParameters,
): Promise<string> {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(order.token) +
        '/initiate-payment',
      params,
    )
    .then((response) => response.data.redirect_url)
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getPaymentOnCheckoutSuccess(
  paymentToken: string,
): Promise<Payment> {
  return api
    .post('payment/' + encodeURIComponent(paymentToken) + '/success')
    .then((response) => Payment.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getPaymentOnCheckoutCancel(
  paymentToken: string,
): Promise<Payment> {
  return api
    .post('payment/' + encodeURIComponent(paymentToken) + '/cancel')
    .then((response) => Payment.fromJson(response.data))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function removeOrderItem(cart: Order, item: OrderItem) {
  return api
    .delete(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(cart.token) +
        '/item/' +
        encodeURIComponent(item.id),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function processIntent(
  order: Order,
  orderItem: OrderItem,
  params: ProcessIntentParameters,
) {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(order.token) +
        '/item/' +
        encodeURIComponent(orderItem.id) +
        '/rental/process-intent',
      params,
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function addSymbolAuthenticationForRental(
  order: Order,
  orderItem: OrderItem,
  pincode: string,
): Promise<Array<Authentication>> {
  return api
    .post(
      'application-environment/:reference/orders/by-token/' +
        encodeURIComponent(order.token) +
        '/item/' +
        encodeURIComponent(orderItem.id) +
        '/rental/authentication/add-symbol',
      { pincode },
    )
    .then((response) =>
      response.data.map((authenticationData: AuthenticationJson) =>
        Authentication.fromJson(authenticationData),
      ),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getReservationInfo(token: string): Promise<ReservationInfo> {
  return api
    .get(
      'application-environment/:reference/reservation/' +
        encodeURIComponent(token),
    )
    .then((response) => ReservationInfo.fromJson(response.data, token))
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function pickupReservation(
  reservation: ReservationInfo,
): Promise<string> {
  return api
    .post(
      'application-environment/:reference/reservation/' +
        encodeURIComponent(reservation.token) +
        '' +
        '/pickup',
    )
    .then((response) => response.data.token)
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getContentBlockForApplicationEnvironment(): Promise<
  Array<ContentBlock>
> {
  return api
    .get('application-environment/:reference/content-blocks')
    .then((response) =>
      response.data.map((item: ContentBlockJson) =>
        ContentBlock.fromJson(item),
      ),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}

export function getContentBlockForLocation(
  locationId: number | string,
): Promise<Array<ContentBlock>> {
  return api
    .get(
      'application-environment/:reference/locations/' +
        encodeURIComponent(locationId) +
        '/content-blocks',
    )
    .then((response) =>
      response.data.map((item: ContentBlockJson) =>
        ContentBlock.fromJson(item),
      ),
    )
    .catch((error) => {
      throw ErrorHandler.convert(error)
    })
}
